import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getQuarter } from '@/helpers/date';
import { fetchOverallReport } from '@/api/insights';
import { format } from 'date-fns';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { getPassthroughRate } from '@/stores/insights/helpers';
import { startCase } from 'lodash-es';
import { assignInsightsTableToCSVDownloadLink } from '@/helpers/fileDownload';
import { numberWithCommas } from '@/helpers/formatters';
import { useDatasetCounts } from './datasetCounts';

export const useOverallReport = defineStore('overallReport', () => {
  const data = ref([]);
  const error = ref(null);

  const isEmptyData = computed(() => {
    if (!Array.isArray(data.value) || data.value.length === 0) {
      return true;
    }

    return data.value.every((category) => {
      // If sources array is missing or empty, treat as empty
      if (!Array.isArray(category.sources) || category.sources.length === 0) {
        return true;
      }

      // Check if all sources in this category have all zero values for the relevant fields
      return category.sources.every((source) => ['review', 'screen', 'assessment', 'offer', 'hired'].every(
        (key) => (source[key] || 0) === 0,
      ));
    });
  });

  const categoryTotals = computed(() => data.value.map((category) => {
    const total = category.sources.reduce((acc, source) => {
      acc.review += source.review || 0;
      acc.screen += source.screen || 0;
      acc.assessment += source.assessment || 0;
      acc.offer += source.offer || 0;
      acc.hired += source.hired || 0;
      return acc;
    }, {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    });

    return {
      category: category.source_category,
      ...total,
    };
  }).sort((a, b) => b.review - a.review));

  const tableRows = computed(() => {
    const rows = categoryTotals.value.map((category) => {
      const {
        category: label, review, screen, assessment, offer, hired,
      } = category;

      return {
        label: startCase(label),
        review: {
          value: review,
          percent: getPassthroughRate('review', 'screen', category),
        },
        screen: {
          value: screen,
          percent: getPassthroughRate('screen', 'assessment', category),
        },
        assessment: {
          value: assessment,
          percent: getPassthroughRate('assessment', 'offer', category),
        },
        offer: {
          value: offer,
          percent: getPassthroughRate('offer', 'hired', category),
        },
        hired: {
          value: hired,
        },
      };
    });

    return rows.sort((a, b) => b.review.value - a.review.value);
  });

  const tableTotals = computed(() => {
    const totalApplied = categoryTotals.value.reduce((acc, item) => acc + item.review, 0);
    const totalScreening = categoryTotals.value.reduce((acc, item) => acc + item.screen, 0);
    const totalAssessment = categoryTotals.value.reduce((acc, item) => acc + item.assessment, 0);
    const totalOffer = categoryTotals.value.reduce((acc, item) => acc + item.offer, 0);
    const totalHired = categoryTotals.value.reduce((acc, item) => acc + item.hired, 0);

    const appliedToScreeningPassthroughRate = getPassthroughRate('applied', 'screening', {
      applied: totalApplied,
      screening: totalScreening,
    });
    const screeningToAssessmentPassthroughRate = getPassthroughRate('screening', 'assessment', {
      screening: totalScreening,
      assessment: totalAssessment,
    });
    const assessmentToOfferPassthroughRate = getPassthroughRate('assessment', 'offer', {
      assessment: totalAssessment,
      offer: totalOffer,
    });
    const offerToHiredPassthroughRate = getPassthroughRate('offer', 'hired', {
      offer: totalOffer,
      hired: totalHired,
    });

    return [
      {
        id: 'applied',
        value: totalApplied,
        percent: appliedToScreeningPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'screening',
        value: totalScreening,
        percent: screeningToAssessmentPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'assessment',
        value: totalAssessment,
        percent: assessmentToOfferPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'offer',
        value: totalOffer,
        percent: offerToHiredPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'hired',
        value: totalHired,
      },
    ];
  });

  const calculateCandidatesForSummary = () => numberWithCommas(categoryTotals.value
    .reduce((acc, item) => acc + item.review, 0));

  const loadOverallReport = async ({
    selectedDateRange,
    routeQuery,
    selectedFilters,
    selectedMyJobs,
  }) => {
    error.value = null;

    const lastQuarter = getQuarter(1);
    const fallbackDateRange = selectedDateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    useAppLoading().setLoading({
      pageLoading: true,
    });

    try {
      const responseData = await fetchOverallReport({
        selectedDateRange: date, routeQuery, filters: selectedFilters, owned: selectedMyJobs,
      });

      data.value = responseData;

      if (responseData?.length) {
        useDatasetCounts().setDatasetCounts(calculateCandidatesForSummary());
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Unable to load overall report - ${err.message}`,
        timeout: 3500,
      });
      error.value = err;
      throw err;
    } finally {
      useAppLoading().setLoading({
        pageLoading: false,
      });
    }
  };

  const downloadOverallCSV = ({ dateRange, projectName }) => {
    assignInsightsTableToCSVDownloadLink({
      tableRows: tableRows.value,
      tableTotals: tableTotals.value,
      dateRange,
      projectName,
      page: 'overall-report',
    });
  };

  return {
    // State
    data,
    error,
    tableRows,
    tableTotals,

    // Computed
    isEmptyData,
    categoryTotals,

    // Actions
    loadOverallReport,
    downloadOverallCSV,
  };
});
